/**
 * Shift Copying Utility Functions
 * 
 * Pure functions for shift copying operations. Extracted from the hook
 * to keep business logic separate from React state management.
 */

import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isAfter, endOfDay, isSameDay, setHours, setMinutes, setSeconds, setMilliseconds } from 'date-fns';
import { DayShift } from '@/types';

export interface CopyResult {
  copiedCount: number;
  errorCount: number;
  skippedCount: number;
  missingDates: Date[];
}

export interface ShiftToInsert {
  business_id: string;
  start_time: string;
  end_time: string;
  title?: string;
  employee_id?: string | null;
  breaks?: any[];
}

/**
 * Validates copying parameters before attempting any operations
 */
export const validateCopyingParameters = (
  sourceDate: Date, 
  businessId: string | null, 
  dayShifts: DayShift[]
): { isValid: boolean; error?: string } => {
  if (!businessId) {
    return { isValid: false, error: 'Business ID is required for copying shifts' };
  }

  if (!sourceDate || isNaN(sourceDate.getTime())) {
    return { isValid: false, error: 'Valid source date is required' };
  }

  if (!dayShifts || dayShifts.length === 0) {
    return { isValid: false, error: 'No shifts found to copy from the source date' };
  }

  return { isValid: true };
};

/**
 * Get all valid target dates for copying (future dates only, excluding source date)
 */
export const getValidTargetDates = (sourceDate: Date): Date[] => {
  const currentMonth = sourceDate;
  const start = startOfMonth(currentMonth);
  const end = endOfMonth(currentMonth);
  const allMonthDates = eachDayOfInterval({ start, end });
  const today = new Date();
  
  return allMonthDates.filter(date => 
    !isSameDay(date, sourceDate) && 
    isAfter(date, endOfDay(today))
  );
};

/**
 * Filter dates by specific weekdays
 */
export const filterDatesByWeekdays = (dates: Date[], weekdays: number[]): Date[] => {
  return dates.filter(date => weekdays.includes(getDay(date)));
};

/**
 * Parse weekday string into array of weekday numbers
 */
export const parseWeekdayString = (weekdayString: string): number[] => {
  const normalizedString = weekdayString.toLowerCase().trim();
  
  switch (normalizedString) {
    case 'weekdays':
      return [1, 2, 3, 4, 5]; // Monday to Friday
    case 'weekends':
      return [0, 6]; // Sunday and Saturday
    case 'mondays':
      return [1];
    case 'tuesdays':
      return [2];
    case 'wednesdays':
      return [3];
    case 'thursdays':
      return [4];
    case 'fridays':
      return [5];
    case 'saturdays':
      return [6];
    case 'sundays':
      return [0];
    default: {
      // Try to parse as comma-separated numbers
      const numbers = normalizedString.split(',')
        .map(n => parseInt(n.trim(), 10))
        .filter(n => !isNaN(n) && n >= 0 && n <= 6);
      return numbers.length > 0 ? numbers : [];
    }
  }
};

/**
 * Create shift insert records by adapting shifts to a target date
 */
export const createShiftInsertRecords = (
  dayShifts: DayShift[],
  targetDate: Date,
  businessId: string
): ShiftToInsert[] => {
  return dayShifts.map(shift => {
    // Get the time components from the original shift
    const originalStart = new Date(shift.startTime);
    const originalEnd = new Date(shift.endTime);
    
    // Create new dates for the target date with the same time
    const newStartTime = setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(new Date(targetDate), originalStart.getHours()),
          originalStart.getMinutes()
        ),
        originalStart.getSeconds()
      ),
      originalStart.getMilliseconds()
    );
    
    const newEndTime = setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(new Date(targetDate), originalEnd.getHours()),
          originalEnd.getMinutes()
        ),
        originalEnd.getSeconds()
      ),
      originalEnd.getMilliseconds()
    );
    
    // Handle case where shift crosses midnight
    if (originalEnd < originalStart) {
      newEndTime.setDate(newEndTime.getDate() + 1);
    }

    return {
      business_id: businessId,
      start_time: newStartTime.toISOString(),
      end_time: newEndTime.toISOString(),
      title: shift.title || 'Copied Shift',
      employee_id: shift.employeeId || null,
      breaks: shift.breaks || []
    };
  });
};

/**
 * Generate user-friendly copy operation summary
 */
export const generateCopySummary = (
  result: CopyResult,
  operation: string,
  sourceDate: Date
): { isSuccess: boolean; title: string; description: string } => {
  const { copiedCount, errorCount, skippedCount, missingDates } = result;
  const totalAttempted = copiedCount + errorCount + skippedCount;
  
  const isSuccess = errorCount === 0 && copiedCount > 0;
  const sourceDateStr = format(sourceDate, 'MMM d, yyyy');

  if (copiedCount === 0 && errorCount > 0) {
    return {
      isSuccess: false,
      title: `${operation} Failed`,
      description: `Failed to copy shifts from ${sourceDateStr}. ${errorCount} error${errorCount !== 1 ? 's' : ''} occurred.`
    };
  }

  if (copiedCount === 0) {
    return {
      isSuccess: false,
      title: `${operation} - No Shifts Copied`,
      description: `No shifts were copied from ${sourceDateStr}. ${skippedCount} date${skippedCount !== 1 ? 's' : ''} were skipped (no valid targets).`
    };
  }

  if (errorCount > 0) {
    return {
      isSuccess: true,
      title: `${operation} Partially Completed`,
      description: `Copied shifts to ${copiedCount} date${copiedCount !== 1 ? 's' : ''} from ${sourceDateStr}. ${errorCount} error${errorCount !== 1 ? 's' : ''} occurred.`
    };
  }

  return {
    isSuccess: true,
    title: `${operation} Completed`,
    description: `Successfully copied shifts to ${copiedCount} date${copiedCount !== 1 ? 's' : ''} from ${sourceDateStr}.`
  };
};

/**
 * Format date for logging purposes
 */
export const formatDateForLogging = (date: Date): string => {
  return format(date, 'yyyy-MM-dd HH:mm:ss');
};

/**
 * Find dates that are missing from the copied shifts results
 */
export const findMissingDates = (targetDates: Date[], copiedStartTimes: string[]): Date[] => {
  if (!copiedStartTimes || copiedStartTimes.length === 0) {
    return [...targetDates];
  }

  return targetDates.filter(targetDate => {
    // Check if any copied shift exists for this target date
    const targetDateStr = format(targetDate, 'yyyy-MM-dd');
    return !copiedStartTimes.some(startTime => {
      const copiedDateStr = format(new Date(startTime), 'yyyy-MM-dd');
      return copiedDateStr === targetDateStr;
    });
  });
};